import type { ComponentPropsWithoutRef, ElementType } from 'react';

import { tv } from '../../lib/tv';
import type { Space } from './space';

export type ContainerProps<T extends ElementType = 'div'> = {
  /** Element to render. Defaults to `div`; use `main`, `section`, `header`, … */
  as?: T;
  /** Drop the 1280px content cap and span the full width (margins still apply). */
  fluid?: boolean;
  /** Vertical padding, as a Figma `space/*` token. Layout sections favour 6-24. */
  paddingY?: Space;
} & Omit<ComponentPropsWithoutRef<T>, 'as' | 'fluid' | 'paddingY'>;

const container = tv({
  // Figma Grid styles: page margin 16 (Mobile) → 32 (Tablet 768) → 48 (Laptop
  // 1280) → 80 (Desktop 1440). Content caps at 1280px - the Desktop grid's
  // content width - and centres beyond it.
  base: 'mx-auto w-full px-4 tablet:px-8 laptop:px-12 desktop:px-20',
  variants: {
    fluid: {
      // The cap only bites at >=1440, where margins are 80: 1440 = 1280 content + 2 x 80.
      false: 'max-w-[1440px]',
      true: 'max-w-none',
    },
    paddingY: {
      0: 'py-0',
      0.5: 'py-0.5',
      1: 'py-1',
      1.5: 'py-1.5',
      2: 'py-2',
      3: 'py-3',
      4: 'py-4',
      5: 'py-5',
      6: 'py-6',
      8: 'py-8',
      10: 'py-10',
      12: 'py-12',
      16: 'py-16',
      20: 'py-20',
      24: 'py-24',
    } satisfies Record<Space, string>,
  },
  defaultVariants: { fluid: false },
});

/**
 * Page-width wrapper implementing the Figma layout grid margins. Put page
 * content (and page-wide bands' inner content) inside it; sidebars sit outside.
 */
export function Container<T extends ElementType = 'div'>({
  as,
  fluid,
  paddingY,
  className,
  ...props
}: ContainerProps<T>) {
  const Component: ElementType = as ?? 'div';
  return <Component className={container({ fluid, paddingY, className })} {...props} />;
}
